import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { saveAICookieForShopify } from '@elem/ui-common/utils/CookieForShopify';
import { Scrollbar } from 'swiper';
import { useScrollAnimation, cn } from 'elem/compPage/battery-backup-for-home/scroll-animation-v2';
import EcoImg from 'eco-design/base/eco-img';
import styles from './index.module.scss';
import { getTabsConfig, TAB_KEY } from './config';

import { useLang } from '@/i18n';
import { getSolarRecommand2 } from '@/service/solar-calculator';
import getLocal from '@/app/utils/i18n';
import gtm from '@/utils/gtm';
import { ENUM_ECO_CART_EVENT, ItfEcoCartItem } from 'eco-design/elements/eco-cart/type.d';
import EcoDesignEventsBus from 'eco-design-micro/EcoDesignEventsBus';

interface ProductCardProps {
    product: any;
    locale: string;
    idx: number;
    isVisible: boolean;
    onAddCart?: (product: any) => void;
    onLearnMore?: (product: any) => void;
}

const ProductCard = ({ product, locale, idx, isVisible, onAddCart, onLearnMore }: ProductCardProps) => {
    const { t } = useLang('powerbackup.Preparing');
    const features = product.desc ? product.desc.split('\n').filter((item: string) => item.trim()) : [];

    const isSoldOut = Number(product?.inventoryQuantity || 0) <= 0 && product?.inventoryPolicy !== 'continue';
    const productUrl = `https://${locale}.ecoflow.com/products/${product.handle}?variant=${product.variantId}`;

    return (
        <div className={cn(isVisible, 'product-card fade-up')} style={{ '--animate-delay': `${idx * 0.15}s` } as React.CSSProperties}>
            <div className="product-image">
                <EcoImg pc={product?.variantImg || product?.img || product?.imgs?.[0]} alt={product.skuTitle} />
            </div>
            <div className="product-content">
                <div className="product-info">
                    <div className="product-label">{product.label}</div>
                    <div className="product-title">{product.skuTitle}</div>
                    {features.length > 0 && (
                        <ul className="product-features">
                            {features.map((feature: string, idx: number) => (
                                <li key={idx}>{feature}</li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="product-actions">
                    {product?.variantId && (
                        <button className="btn-cart" onClick={() => onAddCart?.(product)} disabled={isSoldOut}>
                            {isSoldOut ? t('btn_sold_out') : t('btn1')}
                        </button>
                    )}
                    <a href={productUrl} className="btn-learn" onClick={() => onLearnMore?.(product)}>
                        {t('btn2')}
                    </a>
                </div>
            </div>
        </div>
    );
};

interface ConsultCardProps {
    config: {
        label: string;
        title: string;
        desc: string;
        btn: string;
        img: { pc: string; h5: string };
        href: string;
    };
}

const ConsultCard = ({ config }: ConsultCardProps) => {
    return (
        <div className="consult-card">
            <div className="consult-image">
                <EcoImg pc={config.img.pc} h5={config.img.h5} />
            </div>
            <div className="consult-info">
                <div className="consult-label">{config.label}</div>
                <div className="consult-title">{config.title}</div>
                <div className="consult-desc">{config.desc}</div>
                <a href={config.href} className="consult-btn">
                    {config.btn}
                </a>
            </div>
        </div>
    );
};

export default function Preparing() {
    const { t } = useLang('powerbackup.Preparing');
    const { t: s } = useLang('powerbackup');
    const { locale } = getLocal();
    const [activeTab, setActiveTab] = useState<TAB_KEY>(TAB_KEY.DAYS_5_PLUS);
    const [productInfoMap, setProductInfoMap] = useState<Record<string, any>>({});

    const tabs = useMemo(() => getTabsConfig(t, locale), [t, locale]);
    const currentTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];

    // Tab滑动指示器相关
    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Map<TAB_KEY, HTMLButtonElement>>(new Map());
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

    // 计算indicator位置
    const updateIndicator = useCallback(() => {
        const container = tabsContainerRef.current;
        const activeTabEl = tabRefs.current.get(activeTab);
        if (container && activeTabEl) {
            const containerRect = container.getBoundingClientRect();
            const tabRect = activeTabEl.getBoundingClientRect();
            setIndicatorStyle({
                left: tabRect.left - containerRect.left,
                width: tabRect.width,
            });
        }
    }, [activeTab]);

    // activeTab变化时更新indicator位置
    useEffect(() => {
        updateIndicator();
    }, [updateIndicator]);

    // 窗口resize时重新计算
    useEffect(() => {
        window.addEventListener('resize', updateIndicator);
        return () => window.removeEventListener('resize', updateIndicator);
    }, [updateIndicator]);

    const { ref, isVisible } = useScrollAnimation({
        threshold: 0.1,
    });

    const { ref: ref2, isVisible: isVisible2 } = useScrollAnimation({
        threshold: 0.1,
    });

    useEffect(() => {
        const variantIds = currentTab.products.map((p) => p.variantId);
        if (variantIds.length === 0) return;

        getSolarRecommand2({ variantIds }, locale).then((res: any) => {
            if (res?.data) {
                const map: Record<string, any> = {};
                res.data.forEach((item: any) => {
                    map[item.variantId] = item;
                });
                setProductInfoMap(map);
            }
        });
    }, [currentTab, locale]);

    const products = useMemo(() => {
        return currentTab.products
            .map((config) => {
                const info = productInfoMap[config?.variantId];
                if (!info) return config?.variantId ? null : { ...config };
                return {
                    ...info,
                    label: config.label,
                    desc: config.desc,
                    skuTitle: config.skuTitle || info.skuTitle,
                };
            })
            .filter(Boolean);
    }, [currentTab, productInfoMap]);

    const handleBuy = (card: any) => {
        if (!card?.variantId) return;
        try {
            // @ts-ignore
            window?.fbq && window?.fbq('track', 'Search');
        } catch (e) {
            console.error(e);
        } finally {
            const addCarts: ItfEcoCartItem[] = [
                {
                    price: card?.price || 0,
                    compareAtPrice: card?.compareAtPrice || 0,
                    inventoryQuantity: card?.inventoryQuantity || 0,
                    inventoryPolicy: card?.inventoryPolicy || 'deny',
                    productId: card?.productId,
                    variantId: card?.variantId,
                    variantImg: card?.variantImg || '',
                    img: card?.img || '',
                    skuTitle: card?.skuTitle || '',
                    productTitle: card?.productTitle || '',
                    quantity: 1,
                } as ItfEcoCartItem,
            ];
            EcoDesignEventsBus.emit(ENUM_ECO_CART_EVENT.ECO_CART_ADD, addCarts);
            gtm.push({
                event: 'uaEvent',
                event_name: 'bundle_add_to_cart',
                product_name: card?.title || card?.productTitle || card?.skuTitle,
                list_name: card?.title,
                price: card?.price,
                tag: 'none',
                quantity: 1,
                button_name: s('common_card_buy'),
            });
            saveAICookieForShopify(`https://${locale}.ecoflow.com/cart/${card?.variantId}:1?channel=buy_button`, locale, true);
        }
    };

    const handleLearnMore = (product: any) => {
        saveAICookieForShopify(`https://${locale}.ecoflow.com/products/${product.handle}?variant=${product.variantId}`, locale);

        gtm.push({
            event: 'uaEvent',
            event_name: 'buy',
            product_name: product.skuTitle,
            position: 'In-Products Module',
        });

        gtm.push({
            event: 'uaEvent',
            event_name: 'select_product',
            list_name: currentTab.title,
            product_name: product.skuTitle,
            button_name: t('btn2'),
        });
    };

    return (
        <div ref={ref} className={`${styles.Preparing} GtmModuleData-section`}>
            <div className="pb-layout">
                <h2 className={cn(isVisible, 'power-backup-title fade-up')}>{s('secD_title')}</h2>
            </div>

            {/* 移动端吸顶容器：tabs在这个wrapper内sticky */}
            <div className="preparing-sticky-wrapper">
                <div className=" preparing-tabs-container">
                    <div
                        ref={tabsContainerRef}
                        className={cn(isVisible, 'preparing-tabs fade-up')}
                        style={{ '--animate-delay': '0.15s' } as React.CSSProperties}
                    >
                        {/* 滑动指示器 */}
                        <div
                            className="tab-indicator"
                            style={{
                                transform: `translateX(${indicatorStyle.left}px)`,
                                width: `${indicatorStyle.width}px`,
                            }}
                        />
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                ref={(el) => {
                                    if (el) tabRefs.current.set(tab.key, el);
                                }}
                                className={`tab-item ${activeTab === tab.key ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.tabLabel}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={cn(isVisible, 'preparing-banner fade-up')} style={{ '--animate-delay': '0.3s' } as React.CSSProperties}>
                    <EcoImg className="banner-image" pc={currentTab.image.pc} h5={currentTab.image.h5} />
                    <div className="banner-content pb-x-layout ">
                        <div className="banner-label">{currentTab.label}</div>
                        <h3 className="banner-title">{currentTab.title}</h3>
                        <p className="banner-desc">{currentTab.desc}</p>
                    </div>
                </div>

                <div ref={ref2} className="preparing-products">
                    {/* {currentTab.consultCard && <ConsultCard config={currentTab.consultCard} />} */}
                    <>
                        <div className="products-grid">
                            {products.map((product: any, idx: number) => (
                                <ProductCard
                                    isVisible={isVisible2}
                                    key={idx}
                                    product={product}
                                    locale={locale}
                                    idx={idx}
                                    onAddCart={handleBuy}
                                    onLearnMore={handleLearnMore}
                                />
                            ))}
                        </div>

                        <div className="products-swiper">
                            <Swiper
                                modules={[Scrollbar]}
                                slidesPerView="auto"
                                spaceBetween={16}
                                scrollbar={{
                                    hide: false,
                                    draggable: true,
                                }}
                                breakpoints={{
                                    0: {
                                        spaceBetween: 10,
                                    },
                                    1024: {
                                        spaceBetween: 16,
                                    },
                                }}
                            >
                                {products.map((product: any, idx: number) => (
                                    <SwiperSlide key={idx}>
                                        <ProductCard
                                            product={product}
                                            locale={locale}
                                            idx={idx}
                                            isVisible={isVisible2}
                                            onAddCart={handleBuy}
                                            onLearnMore={handleLearnMore}
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </>
                </div>
            </div>
        </div>
    );
}
